import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Target, X, Save, Trash2, HeartPulse, RotateCcw, Pencil, Rocket } from 'lucide-react'
import { cn } from '@/utils/cn'
import { BdrLeadForm } from './BdrLeadForm'
import { BdrLeadDetails } from './BdrLeadDetails'
import type { BdrLead, BdrCloser } from '@/types'

// ─── Hook type ──────────────────────────────────────────────────────────────

interface BdrHookType {
  user: any
  closers: BdrCloser[]
  partners: any[]
  showModal: boolean
  handleCloseModal: () => void
  isNewLead: boolean
  isEditing: boolean
  setIsEditing: (v: boolean) => void
  selectedLead: BdrLead | null
  newLead: any
  setNewLead: (v: any) => void
  backendErrors: Record<string, string>
  handleAddLead: () => void
  handleUpdateLead: () => void
  handleStartEdit: (lead: BdrLead) => void
  handleDeleteLead: (id: string) => void
  handleMoveToNurturing: (id: string) => void
  handleRecoverLead: (id: string) => void
  handleDiscardLead: (id: string) => void
  getCadencia: (id: string) => any | null
  showRegTentativa: boolean
  setShowRegTentativa: (v: boolean) => void
  regTentativa: any
  setRegTentativa: (v: any) => void
  savingTentativa: boolean
  handleRegTentativa: () => void
  handleAddPending: (text: string, date?: string) => void
  togglePending: (id: number) => void
  removePending: (id: number) => void
}

/** Rodapé view mode: fundo tonal; texto e ícone pretos no tema claro */
const footerBtnLabel =
  'text-zinc-900 [&_svg]:text-zinc-900 [html[data-theme=dark]_&]:text-text-primary [html[data-theme=dark]_&]:[&_svg]:text-text-primary'

// ─── Component ──────────────────────────────────────────────────────────────

export function BdrLeadManagerModal({ hook }: { hook: BdrHookType }) {
  const {
    showModal,
    handleCloseModal,
    isNewLead,
    isEditing,
    selectedLead,
    newLead,
    setNewLead,
    backendErrors,
    handleAddLead,
    handleUpdateLead,
    handleStartEdit,
    handleDeleteLead,
    handleMoveToNurturing,
    handleRecoverLead,
    handleDiscardLead,
    getCadencia,
    closers,
    showRegTentativa,
    setShowRegTentativa,
    regTentativa,
    setRegTentativa,
    savingTentativa,
    handleRegTentativa,
    handleAddPending,
    togglePending,
    removePending,
    user,
    setIsEditing,
  } = hook

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [showModal])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) handleCloseModal()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [showModal, handleCloseModal])

  // Determine view mode
  const isViewMode = selectedLead && !isNewLead && !isEditing
  const isNurturingOrLost = selectedLead?.status === 'nutricao' || selectedLead?.status === 'perdido'
  const isAdmin = user?.role?.toLowerCase() === 'admin'

  const modalContent = (
    <AnimatePresence>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/95 backdrop-blur-[100px]"
            onClick={handleCloseModal}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative z-10 w-full max-w-7xl h-[94vh] rounded-3xl flex flex-col overflow-hidden border border-[color:var(--border)] bg-[var(--bg-card)] shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--border)] bg-[var(--bg-main)] shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Target size={18} className="text-accent" />
                </div>
                <div>
                  <h2 className="text-zinc-900 dark:text-text-primary font-bold text-base">
                    {isNewLead ? 'Novo Lead' : isEditing ? 'Editar Lead' : 'Controle de Leads'}
                  </h2>
                  {selectedLead && !isNewLead && (
                    <p className="text-zinc-700 dark:text-text-secondary text-xs font-semibold">
                      {selectedLead.empresa}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleCloseModal}
                className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 text-text-secondary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body — flex column so BdrLeadDetails can fill height and split 2 cols */}
            <div className="flex flex-1 flex-col min-h-0 overflow-y-auto p-6 custom-scrollbar bg-[var(--bg-main)]">
              {(isNewLead || isEditing) ? (
                <BdrLeadForm
                  newLead={newLead}
                  setNewLead={setNewLead}
                  backendErrors={backendErrors}
                  mode={isNewLead ? 'create' : 'edit'}
                />
              ) : selectedLead ? (
                <BdrLeadDetails
                  selectedLead={selectedLead}
                  getCadencia={getCadencia}
                  closers={closers}
                  showRegTentativa={showRegTentativa}
                  setShowRegTentativa={setShowRegTentativa}
                  regTentativa={regTentativa}
                  setRegTentativa={setRegTentativa}
                  savingTentativa={savingTentativa}
                  handleRegTentativa={handleRegTentativa}
                  handleAddPending={handleAddPending}
                  togglePending={togglePending}
                  removePending={removePending}
                />
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-[color:var(--border)] bg-[var(--bg-main)] shrink-0">
              <div className="flex items-center gap-2">
                {isAdmin && selectedLead && !isNewLead && (
                  <button
                    onClick={() => handleDeleteLead(selectedLead.id)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all min-h-[44px]',
                      'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20',
                      footerBtnLabel,
                    )}
                  >
                    <Trash2 size={14} strokeWidth={2.5} />
                    Excluir Registro
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Create mode */}
                {isNewLead && (
                  <button
                    onClick={handleAddLead}
                    className="flex items-center gap-2 px-6 py-2.5 bg-accent text-white text-xs font-black uppercase tracking-widest rounded-full hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] transition-all min-h-[44px] shadow-lg shadow-accent/20"
                  >
                    <Rocket size={14} className="animate-pulse" />
                    Cadastrar
                  </button>
                )}

                {/* Edit mode */}
                {isEditing && (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/[0.06] text-sm font-bold transition-all min-h-[44px]"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleUpdateLead}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-accent text-white text-sm font-bold rounded-lg hover:brightness-110 transition-all min-h-[44px]"
                    >
                      <Save size={14} />
                      Salvar Alterações
                    </button>
                  </>
                )}

                {/* View mode */}
                {isViewMode && (
                  <>
                    {isNurturingOrLost ? (
                      <>
                        <button
                          onClick={() => handleRecoverLead(selectedLead.id)}
                          className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-teal-400 bg-teal-500/10 hover:bg-teal-500/20 text-sm font-bold transition-all min-h-[44px]"
                        >
                          <RotateCcw size={14} />
                          Resgatar p/ Ativa
                        </button>
                        <button
                          onClick={() => handleDiscardLead(selectedLead.id)}
                          className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-red-400 bg-red-500/10 hover:bg-red-500/20 text-sm font-bold transition-all min-h-[44px]"
                        >
                          <Trash2 size={14} />
                          Descartar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleMoveToNurturing(selectedLead.id)}
                          className={cn(
                            'flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-bold transition-all min-h-[44px]',
                            'bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20',
                            footerBtnLabel,
                          )}
                        >
                          <HeartPulse size={14} strokeWidth={2.5} />
                          Nutrição
                        </button>
                        <button
                          onClick={() => handleStartEdit(selectedLead)}
                          className={cn(
                            'flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-bold transition-all min-h-[44px]',
                            'bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20',
                            footerBtnLabel,
                          )}
                        >
                          <Pencil size={14} strokeWidth={2.5} />
                          Editar Dados
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}
